import { ArrowForward, EditOutlined } from '@mui/icons-material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import {
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from '../../store'
import { AppliancesSelector } from '../../store/Appliances'
import { FormsActions, FormsSelector } from '../../store/forms'
import { StorageKeys } from '../../store/Verification'
import {
  WaterTestKitRequestsSelectors,
  WaterTestKitsActions,
} from '../../store/waterTestKitRequests'
import { PubWaterSystemActions, PubWaterSystemSelector } from '../../store/publicWaterSystem/pubWaterSystem'
import { WaterSystemsActions, WaterSystemsSelector } from '../../store/waterSystems'
import { UserSelector } from '../../store/User' // TODO: remove when v1 strips are fully phased out // SIPP-642
import { SippApi } from '../../services/SippExpress'

export const AboutHomeWater = () => {
  const theme = useTheme()
  const options = useSelector(AppliancesSelector.waterFiltrationOptions)
  const formId = 'water-test'
  const formValues = useSelector((state: RootState) => FormsSelector.getFormValues(state, formId))
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const latestRequest = useSelector(WaterTestKitRequestsSelectors.makeLatestResult)
  const utilityName = useSelector(PubWaterSystemSelector.name)
  const user = useSelector(UserSelector.user) // TODO: remove when v1 strips are fully phased out // SIPP-642

  // SIPP-721: Utility selection state
  const [utilityDialogOpen, setUtilityDialogOpen] = useState(false)
  const [overriddenUtilityName, setOverriddenUtilityName] = useState<string | null>(null)
  const nearbyWaterSystems = useSelector(WaterSystemsSelector.getWaterSystems)
  const nearbyLoading = useSelector(WaterSystemsSelector.loading)

  const displayedUtilityName = overriddenUtilityName ?? utilityName
  const [editingAddress, setEditingAddress] = useState(false)
  const [addressDraft, setAddressDraft] = useState('')
  const [cityDraft, setCityDraft] = useState('')
  const [stateDraft, setStateDraft] = useState('')
  const [zipDraft, setZipDraft] = useState('')

  const handleChange = (e) => {
    const { value, name } = e.target

    dispatch(FormsActions.setValue({ id: formId, [name]: value }))
  }
  const loadLatestRequest = async () => {
    const token = localStorage.getItem(StorageKeys.TOKEN)
    if (token) {
      const body = JSON.parse(atob(token.split('.')[1]))
      const result = await dispatch(
        WaterTestKitsActions.fetchUserWaterTestKitRequestsAsync({
          email: body.email,
          options: { params: { $sort: 'createdAt desc' } },
        }),
      )
      if (WaterTestKitsActions.fetchUserWaterTestKitRequestsAsync.fulfilled.match(result)) {
        const requests = result.payload as any[]
        const latest = requests?.[0]
        if (latest?.utility) {
          dispatch(PubWaterSystemActions.fetchPubWaterSystemAsync({ pwsid: latest.utility }))
        }
      }
    }
  }

  const handleOpenUtilityDialog = () => {
    if (latestRequest?.address) {
      const { city, state, zip } = latestRequest.address
      if (zip) {
        // Zip-based search is the most reliable path
        dispatch(WaterSystemsActions.fetchWaterSystemsAsync({ zips: zip }))
      } else if (city && state) {
        // Fallback: search by city + state when zip is absent
        dispatch(
          WaterSystemsActions.fetchPublicWaterSystemsByAddressComponentAsync({
            city,
            state,
            zipCode: '',
            address1: '',
          }),
        )
      }
    }
    setUtilityDialogOpen(true)
  }

  const handleSelectUtility = (pwsid: string, pwsName: string) => {
    setOverriddenUtilityName(pwsName)
    dispatch(PubWaterSystemActions.setSelectedWaterSystem(pwsid))
    dispatch(PubWaterSystemActions.fetchPubWaterSystemAsync({ pwsid }))
    dispatch(FormsActions.setValue({ id: formId, utility: pwsid }))
    setUtilityDialogOpen(false)
  }

  const submit = async () => {
    const updateRequest = await dispatch(WaterTestKitsActions.updateWaterTestKitRequestAsync({ id: latestRequest._id!, body: formValues as any }))
    if(WaterTestKitsActions.updateWaterTestKitRequestAsync.fulfilled.match(updateRequest)) {
      // Persist home profile fields to user record (non-blocking)
      if (user?._id) {
        SippApi.update.user(user._id, {
          home_age: formValues.home_age,
          systems: formValues.systems,
          waterSourceType: formValues.waterSourceType,
        }).catch((err) => console.error('Failed to update user profile:', err))
      }
      // TODO: remove when v1 strips are fully phased out // SIPP-642
      const nextRoute = user?.stripVersion ? '/test-strip/update' : '/test-strip/strip-version'
      navigate(nextRoute)
    }
  }
  const handleSystemChange = (value) => {
    const b = formValues.systems || []
    let newArr

    // SIPP-828: "None / I don't use a filter" is exclusive — selecting it
    // clears any other choice, and selecting any real filter clears 'none'.
    if (value === 'none') {
      newArr = b.includes('none') ? [] : ['none']
    } else if (b.includes(value)) {
      newArr = b.filter((item) => item !== value)
    } else {
      newArr = [...b.filter((item) => item !== 'none'), value]
    }
    dispatch(FormsActions.setValue({ id: formId, systems: newArr }))
  }
  useEffect(() => {
    if (!formValues || Object.keys(formValues).length === 0) {
      dispatch(
        FormsActions.setValue({
          id: formId,
          home_age: '',
          systems: [],
          waterSourceType: '',
        }),
      )
    }
  }, [dispatch, formValues])
  useEffect(() => {
    loadLatestRequest()
  }, [])

  return (
    <Container
      maxWidth="md"
      sx={{
        justifyContent: 'flex-start',
        alignContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        paddingBottom: '60px',
      }}
    >
      <Typography variant="h2" textAlign="center">
        About your home & water,{' '}
      </Typography>
      <Typography variant="body1" textAlign="center">
        Help us personalize your recommendations for
      </Typography>
      {latestRequest && !editingAddress && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body-small">
              {latestRequest.address!.address1!}
            </Typography>
            <Typography variant="body-small">
              {`${latestRequest.address!.city!}, ${latestRequest.address!.state!} ${latestRequest.address!.zip!}`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setAddressDraft(latestRequest.address!.address1!)
              setCityDraft(latestRequest.address!.city!)
              setStateDraft(latestRequest.address!.state!)
              setZipDraft(latestRequest.address!.zip!)
              setEditingAddress(true)
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Box>
      )}
      {latestRequest && editingAddress && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, maxWidth: 400, width: '100%', mx: 'auto' }}>
          <TextField
            value={addressDraft}
            onChange={(e) => setAddressDraft(e.target.value)}
            size="small"
            fullWidth
            placeholder="Street address"
          />
          <TextField
            value={cityDraft}
            onChange={(e) => setCityDraft(e.target.value)}
            size="small"
            fullWidth
            placeholder="City"
          />
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <TextField
              value={stateDraft}
              onChange={(e) => setStateDraft(e.target.value)}
              size="small"
              fullWidth
              placeholder="State"
            />
            <TextField
              value={zipDraft}
              onChange={(e) => setZipDraft(e.target.value)}
              size="small"
              fullWidth
              placeholder="Zip"
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                const result = await dispatch(
                  WaterTestKitsActions.updateWaterTestKitRequestAsync({
                    id: latestRequest._id!,
                    body: {
                      ...formValues,
                      address: { address1: addressDraft, city: cityDraft, state: stateDraft, zip: zipDraft },
                    } as any,
                  }),
                )
                if (WaterTestKitsActions.updateWaterTestKitRequestAsync.fulfilled.match(result)) {
                  await loadLatestRequest()
                }
                setEditingAddress(false)
              }}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setAddressDraft('')
                setCityDraft('')
                setStateDraft('')
                setZipDraft('')
                setEditingAddress(false)
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {/* Water utility display — hidden for well water users */}
      {formValues.waterSourceType !== 'well' && latestRequest && (
        <Box
          onClick={handleOpenUtilityDialog}
          sx={{
            mt: 2,
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '6px',
            p: 2,
            cursor: 'pointer',
            '&:hover': { borderColor: theme.palette.primary.main },
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            Your water utility
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body1">
              {displayedUtilityName || '—'}
            </Typography>
            <ChevronRightIcon sx={{ color: theme.palette.text.secondary }} />
          </Stack>
          <Typography variant="body-small" color="text.secondary" sx={{ mt: 0.5 }}>
            Based on your address. Tap to change.
          </Typography>
        </Box>
      )}

      {/* Utility selection dialog */}
      <Dialog
        open={utilityDialogOpen}
        onClose={() => setUtilityDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select your water utility</DialogTitle>
        {nearbyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : nearbyWaterSystems.length > 0 ? (
          <List sx={{ pt: 0 }}>
            {nearbyWaterSystems.map((ws) => (
              <ListItemButton
                key={ws.PWSID}
                onClick={() => handleSelectUtility(ws.PWSID, ws.PWS_NAME || ws.PWSID)}
              >
                <ListItemText
                  primary={ws.PWS_NAME || ws.PWSID}
                  secondary={[ws.CITY_NAME, ws.STATE_CODE].filter(Boolean).join(', ')}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No nearby utilities found for this address.
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* Well water checkbox */}
      <Box
        sx={{
          mt: 2,
          backgroundColor: '#f3f6f8',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          px: 2,
          py: 1.75,
          width: '100%',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={formValues.waterSourceType === 'well'}
              onChange={(e) =>
                dispatch(
                  FormsActions.setValue({
                    id: formId,
                    waterSourceType: e.target.checked ? 'well' : 'municipal',
                  }),
                )
              }
            />
          }
          label="I use well water"
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ flexDirection: 'column', display: 'flex' }}>
        <FormControl>
          <FormLabel>How old is your house?</FormLabel>
          <Select
            value={formValues.home_age || ''}
            name="home_age"
            onChange={handleChange}
            displayEmpty
            renderValue={(selected: any) => {
              if (!selected) {
                return (
                  <span style={{ color: '#94a3b880' }}>Selected Estimated Age of your home</span>
                )
              }
              return selected
            }}
            sx={{
              width: '500px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.palette.divider} !important`,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.palette.divider} !important`,
              },
              '& .MuiSelect-icon': {
                color: theme.palette.primary.dark,
              },
              backgroundColor: 'white',
              color: theme.palette.primary.dark,
              borderRadius: '6px',
            }}
          >
            <MenuItem value="" disabled>
              Select Estimated Age of home
            </MenuItem>
            <MenuItem value={'0-5'}>0-5 years old</MenuItem>
            <MenuItem value={'5-10'}>5-10 years old</MenuItem>
            <MenuItem value={'10-15'}>10-15 years old</MenuItem>
            <MenuItem value={'15-20'}>15-20 years old</MenuItem>
            <MenuItem value={'20-25'}>20-25 years old</MenuItem>
            <MenuItem value={'26+'}>26+ years old</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ mt: 2 }}>
          <FormLabel>Do you Currently use any water treament?</FormLabel>
          <Grid container justifyContent="flex-start" spacing={1.5} sx={{ mt: 3 }}>
            {options.map((option) => (
              <SelectButton
                selected={formValues.systems && formValues.systems.includes(option.value)}
                key={option.value}
                value={option.value}
                label={option.label}
                onClick={handleSystemChange}
              />
            ))}
          </Grid>
        </FormControl>
      </Box>
      <AppBar
        position="fixed"
        color="default"
        sx={{ top: 'auto', bottom: 0, height: '60px', justifyContent: 'center' }}
      >
        <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          {/* <Button variant="outlined" size="large" sx={{flexGrow:1}} startIcon={<ArrowBack/>}>Back</Button> */}
          <Button
            variant="contained"
            size="large"
            sx={{ flexGrow: 1 }}
            endIcon={<ArrowForward />}
            onClick={submit}
            disabled={
              !formValues.home_age || (formValues.systems && formValues.systems.length === 0)
            }
          >
            Input your result
          </Button>
        </Container>
      </AppBar>
    </Container>
  )
}

const SelectButton = ({ value, label, onClick, selected }: any) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        p: 2,
        background: selected ? theme.palette.blue[50] : theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        borderColor: selected ? theme.palette.blue[600] : theme.palette.grey[100],
        borderStyle: 'solid',
        borderWidth: selected ? '1px' : '0px',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
      onClick={() => onClick(value)}
    >
      <Typography variant="body1" sx={{ display: 'flex', width: '100%' }}>
        {label}
      </Typography>
    </Box>
  )
}
